<script lang="ts" setup>
import { ModalResultType, NewsItem } from '@/helper/Types'
import { computed, ref } from 'vue'
import { formatDate } from '@/helper/filter'
import { ModalTypes } from '@/helper/ModalTypes'
import { modalService } from '@/services/modals/ModalService'
import ModalParams from '@/services/modals/ModalParams'

const props = defineProps<{
  item: NewsItem
}>()

const emit = defineEmits<{
  (e: 'save', news: NewsItem): void
  (e: 'delete', news: NewsItem): void
}>()

const isEditable = ref<boolean>(false)

const editMode = computed(() => {
  return isEditable.value || !props.item.id
})

function cancelEdit() {
  isEditable.value = false
  if (!props.item.id) {
    emit('delete', props.item)
  }
}

function clickDelete() {
  modalService
    .showModal(
      new ModalParams<any>(ModalTypes.GENERAL, {
        modalTitle: 'Delete news',
        modalText: 'Do you really want to delete this item?',
        confirmButtonText: 'Delete'
      })
    )
    .then(result => {
      if (result.resultType === ModalResultType.CONFIRM) {
        emit('delete', props.item)
      }
    })
}

function clickSave() {
  emit('save', props.item)
  isEditable.value = false
}
</script>

<template>
  <div class="fTable__tr has--td">
    <div class="fTable__td column">
      <div v-if="editMode" class="edit">
        <div v-for="newsItem in item.localizedNews" :key="newsItem" class="news-edit-wrapper">
          <label>Language: {{ newsItem.language }}</label>
          <textarea v-model="newsItem.text" />
        </div>
        <button class="btn is--secondary" @click="cancelEdit()">
          cancel
        </button>
        <button :disabled="item.localizedNews.some(n => !n.text.length)" class="btn is--tertiary" @click="clickSave()">
          save
        </button>
      </div>

      <div v-else class="no-edit" @click="isEditable = true">
        <div v-for="newsItem in item.localizedNews" :key="newsItem">
          <label class="">Language: {{ newsItem.language }}</label>
          <p>{{ newsItem.text }}</p>
        </div>
      </div>
    </div>

    <div v-if="!editMode" class="fTable__td column date">
      <span>{{ formatDate(new Date(item.createdAt)) }}</span>
    </div>

    <div v-if="!editMode" class="fTable__td column actions">
      <button class="icon-btn is--edit" @click="isEditable = true">
        <i class="ic-edit" />
      </button>
      <button class="icon-btn config__delete-btn" @click="clickDelete()">
        <i class="ic-trash" />
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.column {
  label {
    color: $textColorLight;
  }

  &.date {
    justify-content: flex-end;

    span {
      font-size: rem-calc(16);
      color: $textColorLight;
    }
  }

  &:not(:first-child) {
    align-items: flex-start;
    flex-basis: 20%;
  }

  &.actions {
    justify-content: flex-end;
    gap: rem-calc(8px);
  }

  .no-edit {
    overflow: hidden;
    cursor: pointer;

    p {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    div:not(:last-child) {
      margin-bottom: 1rem;
    }
  }

  .edit {
    display: flex;
    flex-wrap: wrap;
    justify-content: right;
    gap: rem-calc(16);
    flex: 1;

    .news-edit-wrapper {
      width: 100%;
    }

    textarea {
      padding: rem-calc(8);
      min-width: 100%;
      max-width: 100%;
      min-height: rem-calc(150);
    }
  }
}
</style>
